import { Link } from "react-router-dom";
import {
  TrendingUp,
  Globe2,
  Target,
  LineChart,
  BarChart3,
  Wallet,
  ShieldCheck,
  Building2,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp, MaskedLines, Eyebrow } from "@/components/Reveal";

const Connector = () => (
  <div className="flex flex-col items-center" aria-hidden>
    <span className="h-8 w-px bg-gradient-to-b from-gold/60 to-gold/20" />
    <span className="mt-1 h-1.5 w-1.5 rotate-45 bg-gold/70" />
    <span className="mt-1 h-8 w-px bg-gradient-to-b from-gold/20 to-gold/60" />
  </div>
);

const ChainNode = ({ children, prominent }) => (
  <div
    className={`border text-center ${
      prominent
        ? "border-gold/50 bg-gold/[0.06] px-8 py-4 font-serif text-2xl tracking-[0.15em] text-[#F5F3EE] sm:text-3xl"
        : "border-[#2a2a2a] bg-[#0d0d0d] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold/90"
    }`}
  >
    {children}
  </div>
);

const STEPS = [
  { n: "01", title: "Strategy", text: "Analisi del Creator, del posizionamento e delle opportunità di crescita." },
  { n: "02", title: "Acquisition", text: "Sviluppo di strategie per aumentare visibilità, traffico e acquisizione di audience." },
  { n: "03", title: "Monetization", text: "Ottimizzazione dei processi commerciali e dei sistemi di monetizzazione." },
  { n: "04", title: "Scale", text: "Analisi delle performance e sviluppo delle opportunità di crescita internazionale." },
];

const EU_USA_CHAIN = [
  { label: "Europe", small: true },
  { label: "Iconic Elite Management", small: false },
  { label: "Dopamine Agency LLC", small: false },
  { label: "USA", small: true },
  { label: "Global Creator Economy", small: true },
];

export default function PartnershipDopamine() {
  const { openContact } = useSite();

  return (
    <main data-testid="partnership-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="partnership-hero">
        <div className="pointer-events-none absolute -right-40 top-24 h-[380px] w-[380px] rounded-full bg-gold/[0.05] blur-[130px]" />
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-32 sm:px-8 lg:pb-20 lg:pt-44">
          <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <FadeUp>
                <Eyebrow>Strategic Partnership</Eyebrow>
              </FadeUp>
              <MaskedLines
                className="mt-8"
                delay={0.15}
                lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-[#F5F3EE]"
                lines={[
                  "Sinergia Internazionale.",
                  <span key="l2" className="gold-text gold-text-animated italic">Risultati Scalabili.</span>,
                ]}
              />
              <FadeUp delay={0.5} className="mt-8 max-w-xl">
                <p className="text-base sm:text-lg leading-relaxed text-[#969696]">
                  ICONIC ELITE MANAGEMENT consolida la propria espansione operativa attraverso una partnership
                  strategica con DOPAMINE AGENCY LLC, società registrata in Delaware, USA, per potenziare
                  l'infrastruttura di marketing e sviluppare nuove opportunità di monetizzazione.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.4} className="flex justify-center lg:justify-end">
              <div className="relative flex flex-col items-center" data-testid="partnership-chain">
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.06] blur-[100px]" />
                <img
                  src="/assets/iem-emblem.png"
                  alt="ICONIC ELITE MANAGEMENT"
                  className="h-20 w-20 rounded-full ring-1 ring-gold/30 sm:h-24 sm:w-24"
                />
                <Connector />
                <ChainNode>Strategy</ChainNode>
                <Connector />
                <div className="border border-[#2a2a2a] bg-[#0d0d0d] p-3 sm:p-4">
                  <img
                    src="/assets/dopamine-logo-v2.png"
                    alt="DOPAMINE AGENCY LLC"
                    data-testid="partnership-dopamine-logo"
                    className="w-44 sm:w-56"
                  />
                </div>
                <Connector />
                <ChainNode>Marketing & Monetization</ChainNode>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* AMBITO DELLA COLLABORAZIONE */}
      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="partnership-ambito">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <FadeUp>
            <Eyebrow>L'ambito della collaborazione</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
              Un'Alleanza <span className="italic text-gold">Operativa Mirata</span>
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-[#969696]">
              Competenze verticali per supportare la crescita, la visibilità e la monetizzazione dei Creator.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-[#1A1A1A] bg-[#1A1A1A] lg:grid-cols-2">
            <FadeUp className="group bg-[#0b0b0b] p-8 lg:p-12 transition-colors duration-500 hover:bg-[#111111]">
              <div data-testid="partnership-card-marketing">
                <div className="flex items-center gap-3 text-gold">
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/25 transition-colors duration-500 group-hover:border-gold/60">
                    <Globe2 size={20} />
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/25 transition-colors duration-500 group-hover:border-gold/60">
                    <TrendingUp size={20} />
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/25 transition-colors duration-500 group-hover:border-gold/60">
                    <Target size={20} />
                  </span>
                </div>
                <div className="mt-7 text-[10px] font-semibold uppercase tracking-[0.28em] text-gold/80">
                  Marketing • Traffic • Acquisition • Global Growth
                </div>
                <h3 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl text-[#F5F3EE]">
                  Marketing Internazionale & Visibilità
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#969696]">
                  Sviluppo di strategie di acquisizione, crescita multi-canale e generazione di traffico profilato a
                  livello internazionale per amplificare la portata e il posizionamento dei brand.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="group bg-[#0b0b0b] p-8 lg:p-12 transition-colors duration-500 hover:bg-[#111111]">
              <div data-testid="partnership-card-monetization">
                <div className="flex items-center gap-3 text-gold">
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/25 transition-colors duration-500 group-hover:border-gold/60">
                    <LineChart size={20} />
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/25 transition-colors duration-500 group-hover:border-gold/60">
                    <BarChart3 size={20} />
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center border border-gold/25 transition-colors duration-500 group-hover:border-gold/60">
                    <Wallet size={20} />
                  </span>
                </div>
                <div className="mt-7 text-[10px] font-semibold uppercase tracking-[0.28em] text-gold/80">
                  Conversion • Revenue • Performance • Analytics
                </div>
                <h3 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl text-[#F5F3EE]">
                  Monetizzazione & Performance
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#969696]">
                  Implementazione di strategie commerciali e ottimizzazione dei funnel di conversione su piattaforme
                  digitali selezionate, con l'obiettivo di migliorare la performance economica dei Creator.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* GOVERNANCE */}
      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="partnership-governance">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <FadeUp>
            <Eyebrow>Governance e rigore operativo</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
              Governance e <span className="italic text-gold">Rigore Operativo</span>
            </h2>
            <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-[#969696]">
              Una partnership internazionale costruita su ruoli chiari, processi organizzati e standard
              professionali.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-[#1A1A1A] bg-[#1A1A1A] lg:grid-cols-2">
            <FadeUp className="group bg-[#0b0b0b] p-8 lg:p-12 transition-colors duration-500 hover:bg-[#111111]">
              <div data-testid="partnership-card-identity">
                <div className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-colors duration-500 group-hover:border-gold/60">
                  <ShieldCheck size={20} />
                </div>
                <h3 className="mt-7 font-serif text-xl sm:text-2xl text-[#F5F3EE]">Identità e Tutela IEM</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#969696]">
                  La gestione dei Creator e il rapporto con gli stessi rimangono strutturati secondo gli standard
                  operativi, di riservatezza e di tutela definiti da ICONIC ELITE MANAGEMENT. La partnership integra
                  competenze e infrastrutture senza modificare l'identità del brand.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="group bg-[#0b0b0b] p-8 lg:p-12 transition-colors duration-500 hover:bg-[#111111]">
              <div data-testid="partnership-card-infrastructure">
                <div className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-colors duration-500 group-hover:border-gold/60">
                  <Building2 size={20} />
                </div>
                <h3 className="mt-7 font-serif text-xl sm:text-2xl text-[#F5F3EE]">Infrastruttura Internazionale</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#969696]">
                  L'integrazione di risorse e competenze tra Europa e Stati Uniti permette di sviluppare
                  un'infrastruttura operativa più ampia, flessibile e orientata alla crescita internazionale.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="partnership-steps">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <FadeUp>
            <Eyebrow>Come funziona la partnership</Eyebrow>
          </FadeUp>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.08} className="relative border-t border-gold/30 pt-6">
                <div data-testid={`partnership-step-${s.n}`}>
                  <span className="font-serif text-3xl text-gold/80">{s.n}</span>
                  <h3 className="mt-4 text-[13px] font-bold uppercase tracking-[0.25em] text-[#F5F3EE]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#969696]">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* EUROPA × USA */}
      <section className="relative overflow-hidden border-t border-[#1A1A1A] py-14 lg:py-20 grain" data-testid="partnership-eu-usa">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[320px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.05] blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-8">
          <FadeUp>
            <Eyebrow className="justify-center">Europa × USA</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-[#F5F3EE]">
              European Strategy. <span className="italic text-gold">American Infrastructure.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#969696]">
              La collaborazione tra ICONIC ELITE MANAGEMENT e DOPAMINE AGENCY LLC combina competenze operative e
              prospettive internazionali per costruire un ecosistema digitale capace di operare oltre i confini
              geografici.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-12 flex flex-col items-center" data-testid="partnership-eu-usa-chain">
            {EU_USA_CHAIN.map((n, i) => (
              <div key={n.label} className="flex flex-col items-center">
                {i > 0 && <Connector />}
                {n.small ? (
                  <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold/80">{n.label}</span>
                ) : (
                  <span className="border border-gold/40 bg-[#0d0d0d] px-7 py-3.5 font-serif text-lg tracking-[0.12em] text-[#F5F3EE] sm:text-xl">
                    {n.label}
                  </span>
                )}
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="relative overflow-hidden border-t border-[#1A1A1A] py-16 lg:py-24 grain" data-testid="partnership-final-cta">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[340px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.05] blur-[130px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-8">
          <FadeUp>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#F5F3EE]">
              Costruisci il tuo percorso con una <span className="gold-text italic">struttura internazionale.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#969696]">
              Accedi a un ecosistema manageriale strutturato, supportato da competenze specializzate e partnership
              operative internazionali.
            </p>
          </FadeUp>
          <FadeUp delay={0.25} className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              data-testid="partnership-cta-creator"
              to="/creator"
              className="inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
            >
              Candidati come Creator <ArrowRight size={15} />
            </Link>
            <button
              data-testid="partnership-cta-contact"
              onClick={() => openContact("creator")}
              className="inline-flex items-center justify-center gap-3 border border-[#2a2a2a] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#F5F3EE] transition-colors duration-300 hover:border-gold/60 hover:text-gold"
            >
              <MessageCircle size={16} /> Contattaci
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
